import { BulbOutlined, RedoOutlined } from "@ant-design/icons";
import { App } from "antd";
import { memo, useCallback, useEffect, useRef, useState } from "react";

import {
    StyledExposureResetBtn,
    StyledExposureRoot,
    StyledExposureSlider,
    StyledExposureSliderWrapper,
    StyledExposureSunBtn,
    StyledExposureTrackLabel,
    StyledExposureTrackWrapper,
    StyledExposureValueBadge,
} from "./Styled";

interface IExposureControl {
    stream: MediaStream | null;
    isReady: boolean;
    focusPoint: { x: number; y: number } | null;
    onUserChange?: (value: number | null) => void;
}

interface ExposureCapabilities {
    min: number;
    max: number;
    step: number;
}

export const ExposureControl = memo(
    ({ stream, isReady, focusPoint, onUserChange }: IExposureControl) => {
        const { notification } = App.useApp();

        const [caps, setCaps] = useState<ExposureCapabilities | null>(null);
        const [value, setValue] = useState(0);
        const [visible, setVisible] = useState(false);
        const hideTimer = useRef<number | null>(null);
        const prevValueRef = useRef(0);

        useEffect(() => {
            if (!stream || !isReady) return;

            const track = stream.getVideoTracks()[0];
            if (!track) return;

            const capabilities =
                track.getCapabilities() as MediaTrackCapabilities & {
                    exposureCompensation?: {
                        min: number;
                        max: number;
                        step: number;
                    };
                };

            if (capabilities.exposureCompensation) {
                const { min, max, step } = capabilities.exposureCompensation;
                setCaps({ min, max, step: step ?? 0.1 });
                setValue(0);
                prevValueRef.current = 0;
            } else {
                setCaps(null);
            }
        }, [stream, isReady]);

        const scheduleHide = useCallback(() => {
            if (hideTimer.current) clearTimeout(hideTimer.current);
            hideTimer.current = window.setTimeout(
                () => setVisible(false),
                3500,
            );
        }, []);

        useEffect(() => {
            if (!focusPoint || !caps) return;

            setVisible(true);
            scheduleHide();
        }, [focusPoint, caps, scheduleHide]);

        const applyExposure = useCallback(
            (ev: number) => {
                if (!stream) return;
                const track = stream.getVideoTracks()[0];

                track
                    ?.applyConstraints({
                        advanced: [
                            {
                                exposureCompensation: ev,
                            } as MediaTrackConstraintSet,
                        ],
                    })
                    .catch(console.warn);
            },
            [stream],
        );

        const handleChange = useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const next = parseFloat(e.target.value);

                if (
                    Math.sign(prevValueRef.current) !== Math.sign(next) &&
                    next === 0
                ) {
                    navigator.vibrate?.(10);
                }

                prevValueRef.current = next;
                setValue(next);
                applyExposure(next);
                scheduleHide();
                onUserChange?.(next === 0 ? null : next);
            },
            [applyExposure, scheduleHide, onUserChange],
        );

        const handleReset = useCallback(() => {
            setValue(0);
            prevValueRef.current = 0;
            applyExposure(0);
            onUserChange?.(null);
            navigator.vibrate?.(10);
            notification.info({
                key: "ev-reset",
                title: "Експозицію скинуто",
                duration: 1.5,
            });
            scheduleHide();
        }, [notification, applyExposure, onUserChange, scheduleHide]);

        const handleSunClick = useCallback(() => {
            setVisible((v) => {
                const next = !v;
                if (next) scheduleHide();
                return next;
            });
        }, [scheduleHide]);

        if (!caps) return null;

        const isNonZero = value !== 0;
        const fmt = (v: number) => (v >= 0 ? `+${v.toFixed(1)}` : v.toFixed(1));

        const maxLabel = `+${Math.floor(caps.max)}`;
        const minLabel = `${Math.ceil(caps.min)}`;

        return (
            <StyledExposureRoot $visible={visible}>
                <StyledExposureSliderWrapper
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <StyledExposureSunBtn
                        $lit={isNonZero}
                        onClick={handleSunClick}
                        title="Експозиція"
                    >
                        <BulbOutlined />
                    </StyledExposureSunBtn>

                    <StyledExposureTrackWrapper>
                        <StyledExposureTrackLabel>
                            {maxLabel}
                        </StyledExposureTrackLabel>

                        <StyledExposureSlider
                            type="range"
                            min={caps.min}
                            max={caps.max}
                            step={caps.step}
                            value={value}
                            onChange={handleChange}
                        />

                        <StyledExposureTrackLabel>
                            {minLabel}
                        </StyledExposureTrackLabel>
                    </StyledExposureTrackWrapper>

                    <StyledExposureValueBadge $nonZero={isNonZero}>
                        {fmt(value)}
                    </StyledExposureValueBadge>

                    <StyledExposureResetBtn
                        $visible={isNonZero}
                        onClick={handleReset}
                        title="Скинути експозицію"
                    >
                        <RedoOutlined />
                    </StyledExposureResetBtn>
                </StyledExposureSliderWrapper>
            </StyledExposureRoot>
        );
    },
);

ExposureControl.displayName = "ExposureControl";
